<template>
    <main class="flex-1 relative overflow-y-auto py-6 focus:outline-none" tabindex="0">
        <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5">
            <div class="md:flex md:items-center md:justify-between">
                <div class="flex-1 min-w-0">
                    <h1 class="py-0.5 text-2xl font-semibold text-gray-900">{{ $t('Products') }}</h1>
                </div>
                <div class="mt-4 flex md:mt-0 md:ml-4">
                    <router-link
                        class="btn btn-blue shadow-sm rounded-md"
                        to="/dashboard/admin/products/new"
                    >
                        {{ $t('Create product') }}
                    </router-link>
                </div>
            </div>
        </div>
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <!-- Search -->
            <div class="mt-4">
                <div class="relative rounded-md shadow-sm max-w-md">
                    <input
                        v-model="search"
                        :placeholder="$t('Search by name or serial number')"
                        class="form-input block w-full sm:text-sm sm:leading-5"
                        @input="debounceSearch"
                    >
                </div>
            </div>
            <div class="my-6 bg-white shadow overflow-hidden sm:rounded-md">
                <loading :status="loading"/>
                <template v-if="productList.length > 0">
                    <ul>
                        <template v-for="(product, index) in productList">
                            <li :class="{'border-t border-gray-200': index !== 0}">
                                <router-link
                                    :to="'/dashboard/admin/products/' + product.id + '/edit'"
                                    class="block hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition duration-150 ease-in-out"
                                >
                                    <div class="px-4 py-4 flex items-center sm:px-6">
                                        <div class="min-w-0 flex-1 sm:flex sm:items-center sm:justify-between">
                                            <div>
                                                <div class="text-sm font-medium leading-5 text-gray-900 truncate">
                                                    {{ product.name }}
                                                </div>
                                                <div class="mt-1 flex items-center text-xs leading-5 text-gray-500">
                                                    <span>S/N: {{ product.serial_number }}</span>
                                                    <span v-if="product.frontend" class="ml-3">{{ product.frontend.name }}</span>
                                                    <span v-if="product.category" class="ml-3">{{ product.category.name }}</span>
                                                </div>
                                            </div>
                                            <div class="mt-4 flex-shrink-0 sm:mt-0">
                                                <div class="flex flex-col items-end text-xs">
                                                    <span v-if="product.client" class="text-gray-500">{{ $t('Client') }}: {{ product.client.name }}</span>
                                                    <span v-if="product.partner" class="text-gray-500">{{ $t('Partner') }}: {{ product.partner.name }}</span>
                                                    <span
                                                        v-if="product.support_expired"
                                                        class="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800"
                                                    >
                                                        {{ $t('Support expired') }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="ml-5 flex-shrink-0">
                                            <svg-vue class="h-5 w-5 text-gray-400" icon="font-awesome.angle-right-regular"></svg-vue>
                                        </div>
                                    </div>
                                </router-link>
                            </li>
                        </template>
                    </ul>
                    <!-- Pagination -->
                    <div v-if="meta.last_page > 1" class="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                        <div class="flex-1 flex justify-between sm:hidden">
                            <button @click="changePage(meta.current_page - 1)" :disabled="meta.current_page <= 1" class="btn btn-secondary">{{ $t('Previous') }}</button>
                            <button @click="changePage(meta.current_page + 1)" :disabled="meta.current_page >= meta.last_page" class="btn btn-secondary">{{ $t('Next') }}</button>
                        </div>
                        <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p class="text-sm leading-5 text-gray-700">
                                    {{ $t('Total') }}: {{ meta.total }}
                                </p>
                            </div>
                            <div>
                                <nav class="relative z-0 inline-flex shadow-sm">
                                    <button
                                        v-for="page in meta.last_page"
                                        :key="page"
                                        @click="changePage(page)"
                                        :class="page === meta.current_page ? 'bg-blue-50 border-blue-500 text-blue-600' : 'bg-white border-gray-300 text-gray-500 hover:text-gray-400'"
                                        class="-ml-px relative inline-flex items-center px-4 py-2 border text-sm leading-5 font-medium focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition ease-in-out duration-150"
                                    >
                                        {{ page }}
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>
                </template>
                <template v-else-if="!loading">
                    <div class="h-full flex">
                        <div class="m-auto">
                            <div class="grid grid-cols-1 justify-items-center h-full w-full px-4 py-10">
                                <div class="flex justify-center items-center">
                                    <svg-vue class="h-full h-auto w-64 mb-12" icon="undraw.browsing"></svg-vue>
                                </div>
                                <div class="flex justify-center items-center">
                                    <div class="w-full font-semibold text-2xl">{{ $t('No records found') }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </main>
</template>

<script>
export default {
    name: "list",
    metaInfo() {
        return {
            title: this.$i18n.t('Products')
        }
    },
    data() {
        return {
            loading: true,
            productList: [],
            search: '',
            searchTimeout: null,
            meta: {
                current_page: 1,
                last_page: 1,
                per_page: 20,
                total: 0,
            },
        }
    },
    mounted() {
        this.getProducts();
    },
    methods: {
        getProducts(page) {
            const self = this;
            self.loading = true;
            let params = {page: page || 1};
            if (self.search) {
                params.search = self.search;
            }
            axios.get('api/dashboard/admin/products', {params}).then(function (response) {
                self.productList = response.data.data;
                self.meta = response.data.meta;
                self.loading = false;
            }).catch(function () {
                self.loading = false;
            });
        },
        changePage(page) {
            if (page >= 1 && page <= this.meta.last_page) {
                this.getProducts(page);
            }
        },
        debounceSearch() {
            const self = this;
            clearTimeout(self.searchTimeout);
            self.searchTimeout = setTimeout(function () {
                self.getProducts(1);
            }, 400);
        },
    }
}
</script>
